import { useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Ban, Plus, Ticket, Trash2 } from 'lucide-react';
import { adminApi, type Pass } from '@/api';
import {
  createPassExpiryDraft,
  isPassExpiryValid,
  PassExpiryPicker,
  toPassExpiryPayload,
} from '@/components/admin/PassExpiryPicker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { formatRub } from '@/lib/utils';

type AdminUser = {
  id: number;
  discordId: string;
  username: string;
  globalName: string | null;
  avatar: string | null;
  minecraftNickname: string | null;
  role: 'user' | 'admin';
  isBanned: boolean;
  banReason: string | null;
  balance: number;
};

export function AdminUsers() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const { data } = useQuery({
    queryKey: ['admin', 'users', q, page],
    queryFn: () => adminApi.users.list(q || undefined, page, 50),
  });

  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState('');
  const [isBanned, setBanned] = useState(false);
  const [deleting, setDeleting] = useState<AdminUser | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const updateMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => adminApi.users.update(editing!.id, body),
    onSuccess: () => {
      toast.success('Сохранено');
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'transactions'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
      qc.invalidateQueries({ queryKey: ['me'] });
      setEditing(null);
    },
    onError: () => toast.error('Не удалось'),
  });

  const deleteMut = useMutation({
    mutationFn: (user: AdminUser) => adminApi.users.remove(user.id),
    onSuccess: (_, deletedUser) => {
      toast.success('Пользователь удалён с сайта');
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
      qc.invalidateQueries({ queryKey: ['admin', 'transactions'] });
      qc.invalidateQueries({ queryKey: ['admin', 'passes'] });
      qc.invalidateQueries({ queryKey: ['admin', 'applications'] });
      if (editing?.id === deletedUser.id) setEditing(null);
      setDeleting(null);
      setDeleteConfirmation('');
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      const code = error.response?.data?.error;
      if (code === 'cannot_delete_self') toast.error('Нельзя удалить собственную учётную запись');
      else if (code === 'cannot_delete_last_admin') toast.error('Нельзя удалить последнего администратора');
      else if (code === 'user_not_found') toast.error('Пользователь уже удалён или не найден');
      else if (code === 'user_deletion_in_progress') toast.error('Удаление пользователя уже выполняется');
      else toast.error(error.response?.data?.message ?? 'Не удалось удалить пользователя');
    },
  });

  const rows: AdminUser[] = (data as { rows?: AdminUser[] })?.rows ?? [];
  const total = (data as { total?: number })?.total ?? 0;
  const currentUserId = qc.getQueryData<{ id: number }>(['me'])?.id;

  function openDeleteDialog(user: AdminUser) {
    setDeleting(user);
    setDeleteConfirmation('');
  }

  function closeDeleteDialog() {
    if (deleteMut.isPending) return;
    setDeleting(null);
    setDeleteConfirmation('');
  }

  return (
    <div>
      <h1 className="text-3xl font-extrabold text-eol-white mb-6">Пользователи</h1>
      <div className="flex gap-3 mb-4">
        <Input
          placeholder="Поиск (никнейм, Discord ID, имя пользователя)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-md"
        />
        <Button variant="secondary" onClick={() => setPage(1)}>
          Найти
        </Button>
      </div>

      <div className="space-y-2">
        {rows.map((u) => (
          <Card key={u.id}>
            <CardContent className="pt-3 pb-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                {u.avatar && <img src={u.avatar} alt="" className="h-9 w-9 rounded-full" />}
                <div>
                  <p className="font-semibold text-eol-white">{u.globalName ?? u.username}</p>
                  <p className="text-xs text-eol-desc">
                    {u.discordId} {u.minecraftNickname && `· ${u.minecraftNickname}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {u.role === 'admin' && <Badge>Админ</Badge>}
                {u.isBanned && <Badge variant="danger">Бан</Badge>}
                <span className="text-eol-pink font-bold">{formatRub(u.balance)}</span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setEditing(u);
                    setDelta(0);
                    setReason(u.banReason ?? '');
                    setBanned(u.isBanned);
                  }}
                >
                  Редактировать
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => openDeleteDialog(u)}
                  disabled={u.id === currentUserId}
                  title={u.id === currentUserId ? 'Нельзя удалить свою учётную запись' : 'Удалить пользователя'}
                  aria-label={`Удалить пользователя ${u.username}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <p className="mt-4 text-sm text-eol-desc">Всего: {total}</p>

      <Dialog open={editing != null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing?.globalName ?? editing?.username}
              {editing?.minecraftNickname && (
                <span className="ml-2 text-eol-desc text-base font-normal">
                  / {editing.minecraftNickname}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {editing && (
            <div className="space-y-6">
              <section className="space-y-3">
                <h3 className="text-xs uppercase font-semibold tracking-wider text-eol-desc">
                  Профиль
                </h3>
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-eol-desc">
                    Дельта баланса (₽, можно отрицательная)
                  </span>
                  <Input type="number" value={delta} onChange={(e) => setDelta(Number(e.target.value))} />
                </label>
                <label className="flex items-center gap-2 text-eol-white">
                  <input
                    type="checkbox"
                    checked={isBanned}
                    onChange={(e) => setBanned(e.target.checked)}
                  />
                  Заблокирован
                </label>
                {isBanned && (
                  <Input placeholder="Причина" value={reason} onChange={(e) => setReason(e.target.value)} />
                )}
                <Button variant="outline" onClick={() => updateMut.mutate({ unlockNickname: true })}>
                  Снять блокировку ника (разрешить выбрать заново)
                </Button>
              </section>

              <PassesSection user={editing} />
            </div>
          )}

          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Отмена
            </Button>
            <Button
              onClick={() =>
                updateMut.mutate({
                  balanceDeltaMinor: delta * 100,
                  isBanned,
                  banReason: isBanned ? reason : null,
                })
              }
            >
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleting != null} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-eol-red" /> Удалить пользователя?
            </DialogTitle>
            <DialogDescription>
              Профиль <strong className="text-eol-white">{deleting?.globalName ?? deleting?.username}</strong>{' '}
              будет удалён с сайта. Это действие нельзя отменить.
            </DialogDescription>
          </DialogHeader>

          {deleting && (
            <div className="space-y-3">
              <div className="rounded-md border border-eol-red/20 bg-eol-red/5 p-3 text-sm text-eol-desc">
                <p>Discord ID: <code className="text-eol-white">{deleting.discordId}</code></p>
                {deleting.minecraftNickname && (
                  <p className="mt-1">Minecraft-ник: <strong className="text-eol-white">{deleting.minecraftNickname}</strong></p>
                )}
              </div>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-eol-desc">
                  Для подтверждения введите {deleting.username}
                </span>
                <Input
                  value={deleteConfirmation}
                  onChange={(event) => setDeleteConfirmation(event.target.value)}
                  placeholder={deleting.username}
                  autoComplete="off"
                  className="mt-1"
                />
              </label>
            </div>
          )}

          <DialogFooter className="flex-wrap">
            <Button variant="secondary" onClick={closeDeleteDialog} disabled={deleteMut.isPending}>
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleting && deleteMut.mutate(deleting)}
              disabled={
                !deleting ||
                deleteMut.isPending ||
                deleteConfirmation.trim() !== deleting.username
              }
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              {deleteMut.isPending ? 'Удаление…' : 'Удалить пользователя'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PassesSection({ user }: { user: AdminUser }) {
  const qc = useQueryClient();
  const passesQuery = useInfiniteQuery({
    queryKey: ['admin', 'passes', 'by-user', user.id],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      adminApi.passes.list({ userId: user.id, status: 'all', page: pageParam, perPage: 100 }),
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((total, page) => total + page.rows.length, 0);
      return lastPage.rows.length > 0 && loaded < Number(lastPage.total)
        ? pages.length + 1
        : undefined;
    },
  });
  const servers = useQuery({ queryKey: ['admin', 'servers'], queryFn: adminApi.servers.list });

  const [openGrant, setOpenGrant] = useState(false);
  const [serverId, setServerId] = useState('');
  const [expiry, setExpiry] = useState(() => createPassExpiryDraft());
  const [note, setNote] = useState('');

  const grantMut = useMutation({
    mutationFn: () =>
      adminApi.passes.grant({
        userId: user.id,
        serverId: serverId ? Number(serverId) : null,
        note: note || null,
        ...toPassExpiryPayload(expiry),
      }),
    onSuccess: (result) => {
      toast.success(result.operation === 'extended' ? 'Срок проходки увеличен' : 'Проходка выдана');
      qc.invalidateQueries({ queryKey: ['admin', 'passes', 'by-user', user.id] });
      qc.invalidateQueries({ queryKey: ['admin', 'passes'] });
      setOpenGrant(false);
      setServerId('');
      setExpiry(createPassExpiryDraft());
      setNote('');
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string; message?: string } } };
      const code = err.response?.data?.error;
      if (code === 'no_nickname') toast.error('У пользователя не привязан Minecraft-ник');
      else if (code === 'server_not_found') toast.error('Сервер не найден');
      else if (code === 'pass_is_infinite') toast.error('У пользователя уже есть бессрочная проходка');
      else if (code === 'pass_scope_ambiguous') {
        toast.error('У пользователя несколько проходок. Выберите конкретный сервер для продления');
      }
      else toast.error(err.response?.data?.message ?? 'Ошибка');
    },
  });

  const revokeMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => adminApi.passes.revoke(id, reason),
    onSuccess: () => {
      toast.success('Проходка отозвана');
      qc.invalidateQueries({ queryKey: ['admin', 'passes', 'by-user', user.id] });
      qc.invalidateQueries({ queryKey: ['admin', 'passes'] });
    },
    onError: () => toast.error('Не удалось отозвать'),
  });

  useEffect(() => {
    if (passesQuery.hasNextPage && !passesQuery.isFetchingNextPage) {
      void passesQuery.fetchNextPage();
    }
  }, [passesQuery.data?.pages.length, passesQuery.fetchNextPage, passesQuery.hasNextPage, passesQuery.isFetchingNextPage]);

  const passes = passesQuery.data?.pages.flatMap((page) => page.rows) ?? [];
  // Сортируем: активные сверху (по дате создания desc), потом отозванные/истёкшие.
  const sorted = [...passes].sort((a, b) => {
    const aActive = isPassActive(a) ? 0 : 1;
    const bActive = isPassActive(b) ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;
    return b.id - a.id;
  });

  const canGrant = Boolean(user.minecraftNickname);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-xs uppercase font-semibold tracking-wider text-eol-desc flex items-center gap-2">
          <Ticket className="h-3.5 w-3.5" /> Проходки
        </h3>
        <Button
          size="sm"
          onClick={() => {
            if (openGrant) {
              setOpenGrant(false);
            } else {
              setExpiry(createPassExpiryDraft());
              setOpenGrant(true);
            }
          }}
          disabled={!canGrant}
          title={canGrant ? '' : 'Сначала привяжите Minecraft-ник'}
          className="gap-1.5"
        >
          <Plus className="h-4 w-4" /> Выдать
        </Button>
      </div>

      {!canGrant && (
        <p className="text-xs text-eol-desc">У пользователя не привязан Minecraft-ник - выдача недоступна.</p>
      )}

      {openGrant && (
        <div className="rounded-md border border-white/10 bg-eol-black/40 p-3 space-y-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-eol-desc">Сервер</span>
              <Select value={serverId} onChange={(e) => setServerId(e.target.value)}>
                <option value="">Все серверы (глобально)</option>
                {(servers.data as Array<{ id: number; name: string }> | undefined)?.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </label>
          </div>
          <PassExpiryPicker
            value={expiry}
            onChange={setExpiry}
            disabled={grantMut.isPending}
          />
          <Input placeholder="Заметка (для истории)" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="secondary" onClick={() => setOpenGrant(false)}>
              Отмена
            </Button>
            <Button
              size="sm"
              onClick={() => grantMut.mutate()}
              disabled={grantMut.isPending || !isPassExpiryValid(expiry)}
            >
              {expiry.mode === 'duration' ? 'Продлить проходку' : 'Выдать проходку'}
            </Button>
          </div>
        </div>
      )}

      {passesQuery.isLoading && <p className="text-eol-desc text-sm">Загрузка…</p>}
      {!passesQuery.isLoading && sorted.length === 0 && (
        <p className="text-eol-desc text-sm">Проходок ещё не было.</p>
      )}

      {sorted.length > 0 && (
        <div className="max-h-72 space-y-2 overflow-y-auto overscroll-contain pr-1">
          {sorted.map((p) => (
            <div
              key={p.id}
              className="flex items-center justify-between gap-3 rounded-md border border-white/10 bg-eol-black/30 px-3 py-2 flex-wrap"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <PassStatusBadge pass={p} />
                  {p.serverName ? (
                    <Badge variant="secondary">{p.serverName}</Badge>
                  ) : (
                    <Badge variant="secondary">Все серверы</Badge>
                  )}
                  <Badge variant="secondary">{sourceLabel(p.source)}</Badge>
                </div>
                <p className="text-xs text-eol-desc mt-1">
                  Создана {fmt(p.createdAt)} · {p.expiresAt ? `истекает ${fmt(p.expiresAt)}` : 'бессрочно'}
                  {p.revokedAt && ` · отозвана ${fmt(p.revokedAt)}${p.revokeReason ? ` (${p.revokeReason})` : ''}`}
                  {p.note ? ` · ${p.note}` : ''}
                </p>
              </div>
              {!p.revokedAt && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  disabled={revokeMut.isPending}
                  onClick={() => {
                    const r = prompt(`Причина отзыва проходки ${p.nickname}?`, '');
                    if (r === null) return;
                    revokeMut.mutate({ id: p.id, reason: r });
                  }}
                >
                  <Ban className="h-4 w-4" /> Отозвать
                </Button>
              )}
            </div>
          ))}
          {passesQuery.isFetchingNextPage && (
            <p className="py-2 text-center text-xs text-eol-desc">Загружаем историю…</p>
          )}
        </div>
      )}
    </section>
  );
}

function PassStatusBadge({ pass }: { pass: Pass }) {
  if (pass.revokedAt) return <Badge variant="danger">Отозвана</Badge>;
  if (pass.expiresAt && new Date(pass.expiresAt) <= new Date())
    return <Badge variant="danger">Истекла</Badge>;
  return <Badge>Активна</Badge>;
}

function isPassActive(pass: Pass): boolean {
  if (pass.revokedAt) return false;
  if (pass.expiresAt && new Date(pass.expiresAt) <= new Date()) return false;
  return true;
}

function sourceLabel(s: Pass['source']): string {
  return s === 'order' ? 'Покупка' : s === 'manual' ? 'Вручную' : 'Импорт';
}

function fmt(d: string | null): string {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleString('ru-RU');
  } catch {
    return d;
  }
}
